import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { format } from "date-fns";

import {
  Box,
  Button,
  Checkbox,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Autocomplete,
} from "@mui/material";

import useData from "../../hooks/useData";
import useProject from "../../hooks/useProject";
import usePage from "../../hooks/usePage";

import Page from "./Page";
import Card from "../layout/Card";
import DateField from "../elements/Date";
import Dialog_YesNo from "../ui/Dialog_YesNo";

const BulkCallEditor = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { getFromServer, executeAction, addToWorkQueue } = useData();
  const { showMessage } = usePage();
  const { callEntity } = useProject();

  // Get project ID from route params or location state
  const projectId =
    new URLSearchParams(location.search).get("projectId") ||
    location.state?.projectId;

  const currentUserId = useSelector((state) => state.authentication.userId);
  const email = useSelector((state) => state.authentication.email);

  // Filter state
  const [repId, setRepId] = useState("");
  const [territoryId, setTerritoryId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Analysis state
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedCallIds, setSelectedCallIds] = useState([]);

  // Action state
  const [action, setAction] = useState("");
  const [rescheduleType, setRescheduleType] = useState("setDate");
  const [rescheduleValue, setRescheduleValue] = useState("");
  const [newRepId, setNewRepId] = useState("");

  // Data state
  const [project, setProject] = useState(null);
  const [reps, setReps] = useState([]);
  const [assignableReps, setAssignableReps] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Dialog state
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadProjectData();
    }
  }, [projectId]);

  const loadProjectData = async () => {
    try {
      setIsLoading(true);

      // Get project
      const projectData = await getFromServer({
        entityName: "Project",
        id: projectId,
      });

      if (projectData) {
        setProject(projectData);

        // Get reps for this project using executeAction
        const repsResponse = await executeAction({
          entityName: "Call",
          action: "getScheduledReps",
          data: { projectId },
        });

        if (repsResponse?.isSuccessful && Array.isArray(repsResponse.data)) {
          setReps(repsResponse.data);
        } else {
          setReps([]);
        }

        // Get all active reps for reassignment target selector
        const allReps = await getFromServer({
          entityName: "User",
          filter: { isActive: true, role: "rep" },
        });

        if (Array.isArray(allReps)) {
          const formattedReps = allReps
            .map((user) => ({
              id: user.id || user._id,
              name:
                user.legalName ||
                user.name ||
                [user.firstName, user.lastName].filter(Boolean).join(" ") ||
                user.email ||
                "(no name)",
            }))
            .filter((user) => !!user.id)
            .sort((a, b) => a.name.localeCompare(b.name));

          setAssignableReps(formattedReps);
        } else {
          setAssignableReps([]);
        }

        // Get territories for this project
        const territoriesData = await getFromServer({
          entityName: "Territory",
          filter: { projectId },
        });

        if (Array.isArray(territoriesData)) {
          const formattedTerritories = territoriesData.map((territory) => ({
            id: territory.id || territory._id,
            name: territory.name,
          }));
          setTerritories(formattedTerritories);
        } else {
          setTerritories([]);
        }
      }

      setIsLoading(false);
    } catch (err) {
      console.error("Error loading project data:", err);
      showMessage("Error loading project data");
      setIsLoading(false);
    }
  };

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);

      const criteria = {
        projectId,
        repId: repId || null,
        territoryId: territoryId || null,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
      };

      const result = await executeAction({
        entityName: "Call",
        action: "bulkAnalyze",
        data: criteria,
      });

      if (result?.isSuccessful && result.data) {
        setAnalysis(result.data);
        const previewIds = Array.isArray(result.data.preview)
          ? result.data.preview
              .map((call) => String(call?._id || ""))
              .filter(Boolean)
          : [];
        setSelectedCallIds(previewIds);
      } else {
        setAnalysis(null);
        setSelectedCallIds([]);
        showMessage(result?.message || "Failed to analyze calls");
      }
      setIsAnalyzing(false);
    } catch (err) {
      console.error("Error analyzing calls:", err);
      showMessage("Error analyzing calls");
      setIsAnalyzing(false);
    }
  };

  const handleExecuteAction = async () => {
    setConfirmDialog(false);

    if (!action || !analysis) {
      showMessage("No action selected or analysis data missing");
      return;
    }

    try {
      setIsExecuting(true);

      const criteria = {
        projectId,
        repId: repId || null,
        territoryId: territoryId || null,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
        callIds: selectedCallIds,
      };

      if (action === "reschedule") {
        // Queue reschedule via worker
        const rescheduleData = {
          type: rescheduleType,
          value:
            rescheduleType === "setDate"
              ? rescheduleValue
              : parseInt(rescheduleValue),
        };

        const workQueueData = {
          ...criteria,
          rescheduleData,
        };

        await addToWorkQueue("bulkRescheduleByProject", workQueueData);
        showMessage(
          "Your bulk reschedule job has been queued. You will receive an email when complete.",
        );
      } else if (action === "delete") {
        // Queue delete via worker
        await addToWorkQueue("bulkDeleteByProject", criteria);
        showMessage(
          "Your bulk delete job has been queued. You will receive an email when complete.",
        );
      } else if (action === "changeRep") {
        // Queue change rep via worker
        await addToWorkQueue("bulkChangeRepByProject", {
          ...criteria,
          newRepId,
        });
        showMessage(
          "Your bulk assignment job has been queued. You will receive an email when complete.",
        );
      }

      // Reset form
      setAnalysis(null);
      setAction("");
      setRescheduleType("setDate");
      setRescheduleValue("");
      setNewRepId("");
      setSelectedCallIds([]);

      setIsExecuting(false);
    } catch (err) {
      console.error("Error executing action:", err);
      showMessage("Error executing action");
      setIsExecuting(false);
    }
  };

  const handleToolbarClick = (action) => {
    if (action === "back") {
      navigate("/projectschedule", { state: { projectId } });
    }
  };

  const getActionConfirmMessage = () => {
    const actionName =
      action === "delete"
        ? "DELETE"
        : action === "reschedule"
          ? "RESCHEDULE"
          : "REASSIGN";

    return `You are about to ${actionName} **${selectedCallIds.length} selected calls**. This cannot be undone. Continue?`;
  };

  const toggleCallSelection = (callId) => {
    const id = String(callId || "");
    if (!id) return;

    setSelectedCallIds((current) => {
      if (current.includes(id)) {
        return current.filter((item) => item !== id);
      }
      return [...current, id];
    });
  };

  const toggleAllPreviewSelection = () => {
    const previewIds = Array.isArray(analysis?.preview)
      ? analysis.preview.map((call) => String(call?._id || "")).filter(Boolean)
      : [];

    if (previewIds.length === 0) {
      setSelectedCallIds([]);
      return;
    }

    const allSelected = previewIds.every((id) => selectedCallIds.includes(id));
    setSelectedCallIds(allSelected ? [] : previewIds);
  };

  if (isLoading) {
    return (
      <Page
        entityName={project?.name || "Project"}
        pageName="Bulk Call Editor"
        includeToolbar={true}
        items={[
          {
            name: "back",
            iconName: "ArrowCircleLeft",
          },
        ]}
        onToolItemClick={handleToolbarClick}
      >
        <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  return (
    <Page
      entityName={project?.name || "Project"}
      pageName="Bulk Call Editor"
      includeToolbar={true}
      items={[
        {
          name: "back",
          iconName: "ArrowCircleLeft",
        },
      ]}
      onToolItemClick={handleToolbarClick}
    >
      <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
        {/* Filter Panel */}
        <Card title="Filter Calls">
          <Stack
            spacing={2}
            sx={{ padding: 2 }}
          >
            <Autocomplete
              options={reps}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : `${option.name} (${option.count})`
              }
              value={reps.find((r) => r.id === repId) || null}
              onChange={(event, newValue) => {
                setRepId(newValue?.id || "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Rep"
                  variant="outlined"
                />
              )}
              noOptionsText="No reps found"
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <Autocomplete
              options={territories}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.name
              }
              value={territories.find((t) => t.id === territoryId) || null}
              onChange={(event, newValue) => {
                setTerritoryId(newValue?.id || "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Territory"
                  variant="outlined"
                />
              )}
              noOptionsText="No territories found"
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <DateField
                  label="Date From"
                  value={dateFrom}
                  onChange={(newValue) => setDateFrom(newValue)}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <DateField
                  label="Date To"
                  value={dateTo}
                  onChange={(newValue) => setDateTo(newValue)}
                />
              </Box>
            </Box>

            <Button
              variant="contained"
              onClick={handleAnalyze}
              disabled={isAnalyzing}
            >
              {isAnalyzing ? <CircularProgress size={24} /> : "Analyze"}
            </Button>
          </Stack>
        </Card>

        {/* Analysis Results */}
        {analysis && (
          <Card title="Analysis Results">
            <Stack
              spacing={2}
              sx={{ padding: 2 }}
            >
              <Typography variant="h6">
                Calls Affected: <strong>{analysis.affectedCount}</strong>
              </Typography>

              {analysis.affectedCount > 0 && (
                <>
                  <Typography variant="subtitle2">Preview:</Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={
                                Array.isArray(analysis?.preview) &&
                                analysis.preview.length > 0 &&
                                analysis.preview.every((call) =>
                                  selectedCallIds.includes(
                                    String(call?._id || ""),
                                  ),
                                )
                              }
                              indeterminate={
                                selectedCallIds.length > 0 &&
                                Array.isArray(analysis?.preview) &&
                                analysis.preview.some((call) =>
                                  selectedCallIds.includes(
                                    String(call?._id || ""),
                                  ),
                                ) &&
                                !analysis.preview.every((call) =>
                                  selectedCallIds.includes(
                                    String(call?._id || ""),
                                  ),
                                )
                              }
                              onChange={toggleAllPreviewSelection}
                            />
                          </TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Location #</TableCell>
                          <TableCell>City</TableCell>
                          <TableCell>Rep</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Territory</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analysis.preview.map((call) => (
                          <TableRow key={call._id}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedCallIds.includes(
                                  String(call?._id || ""),
                                )}
                                onChange={() => toggleCallSelection(call?._id)}
                              />
                            </TableCell>
                            <TableCell>{call.locationName}</TableCell>
                            <TableCell>{call.locationNumber || "-"}</TableCell>
                            <TableCell>{call.city || "-"}</TableCell>
                            <TableCell>{call.repName}</TableCell>
                            <TableCell>{call.callStatus || "-"}</TableCell>
                            <TableCell>
                              {call.scheduleDate
                                ? format(
                                    new Date(call.scheduleDate),
                                    "MMM dd, yyyy",
                                  )
                                : "-"}
                            </TableCell>
                            <TableCell>{call.territoryName}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Stack>
          </Card>
        )}

        {/* Action Selection */}
        {analysis && analysis.affectedCount > 0 && (
          <Card title="Select Action">
            <Stack
              spacing={2}
              sx={{ padding: 2 }}
            >
              <TextField
                select
                label="Action"
                value={action}
                onChange={(e) => setAction(e.target.value)}
                variant="outlined"
                fullWidth
              >
                <MenuItem value="">Choose an action...</MenuItem>
                <MenuItem value="delete">Delete Calls</MenuItem>
                <MenuItem value="reschedule">Reschedule Calls</MenuItem>
                <MenuItem value="changeRep">Change Rep</MenuItem>
              </TextField>

              {action === "reschedule" && (
                <>
                  <TextField
                    select
                    label="Reschedule Type"
                    value={rescheduleType}
                    onChange={(e) => setRescheduleType(e.target.value)}
                    variant="outlined"
                    fullWidth
                  >
                    <MenuItem value="setDate">Set to Specific Date</MenuItem>
                    <MenuItem value="relative">Relative (Days)</MenuItem>
                  </TextField>

                  {rescheduleType === "setDate" ? (
                    <DateField
                      label="New Date"
                      value={rescheduleValue}
                      onChange={(newValue) => setRescheduleValue(newValue)}
                    />
                  ) : (
                    <TextField
                      label="Days (+ or -)"
                      type="number"
                      value={rescheduleValue}
                      onChange={(e) => setRescheduleValue(e.target.value)}
                      variant="outlined"
                      fullWidth
                      placeholder="e.g., 5 or -3"
                    />
                  )}
                </>
              )}

              {action === "changeRep" && (
                <Autocomplete
                  options={assignableReps}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.name
                  }
                  value={assignableReps.find((r) => r.id === newRepId) || null}
                  onChange={(event, newValue) => {
                    setNewRepId(newValue?.id || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="New Rep"
                      variant="outlined"
                    />
                  )}
                  noOptionsText="No reps found"
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                />
              )}

              <Button
                variant="contained"
                color="error"
                onClick={() => setConfirmDialog(true)}
                disabled={
                  !action || isExecuting || selectedCallIds.length === 0
                }
              >
                {isExecuting ? (
                  <CircularProgress size={24} />
                ) : (
                  "Execute Action"
                )}
              </Button>
            </Stack>
          </Card>
        )}
      </Box>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <Dialog_YesNo
          title={`Confirm ${action.toUpperCase()}`}
          message={getActionConfirmMessage()}
          open={confirmDialog}
          onClose={() => setConfirmDialog(false)}
          onYes={handleExecuteAction}
          onNo={() => setConfirmDialog(false)}
        />
      )}
    </Page>
  );
};

export default BulkCallEditor;
